<script setup>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useAlert } from "dashboard/composables";
import Button from "dashboard/components-next/button/Button.vue";
import Icon from "dashboard/components-next/icon/Icon.vue";

const props = defineProps({
  announcements: {
    type: Array,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const currentIndex = ref(0);
const clampedIndex = computed(() =>
  Math.min(currentIndex.value, props.announcements.length - 1),
);
const current = computed(() => props.announcements[clampedIndex.value]);

watch(
  () => props.announcements.length,
  (length) => {
    if (currentIndex.value > length - 1) {
      currentIndex.value = Math.max(0, length - 1);
    }
  },
);

const goPrevious = () => {
  if (currentIndex.value > 0) currentIndex.value -= 1;
};
const goNext = () => {
  if (currentIndex.value < props.announcements.length - 1)
    currentIndex.value += 1;
};

const isSubmitting = ref(false);

const acknowledge = async () => {
  isSubmitting.value = true;
  try {
    await store.dispatch("announcements/acknowledge", current.value.id);
  } catch {
    useAlert(t("ANNOUNCEMENTS.MODAL.ACTION_FAILED"));
  } finally {
    isSubmitting.value = false;
  }
};

const dismiss = async () => {
  isSubmitting.value = true;
  try {
    await store.dispatch("announcements/dismiss", current.value.id);
  } catch {
    useAlert(t("ANNOUNCEMENTS.MODAL.ACTION_FAILED"));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
  >
    <div class="w-full max-w-lg rounded-xl bg-n-solid-1 p-6 shadow-xl">
      <div class="flex items-center justify-between gap-2">
        <span
          class="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/20 dark:text-blue-400"
        >
          <Icon icon="i-lucide-info" class="size-3" />
          {{ t("ANNOUNCEMENTS.LEVEL.notice") }}
        </span>
        <span v-if="announcements.length > 1" class="text-xs text-n-slate-11">
          {{
            t("ANNOUNCEMENTS.MODAL.COUNTER", {
              current: clampedIndex + 1,
              total: announcements.length,
            })
          }}
        </span>
      </div>

      <h3 class="mt-3 text-lg font-medium text-n-slate-12">
        {{ current.title }}
      </h3>
      <div
        v-dompurify-html="current.content_html"
        class="prose prose-bubble max-w-none mt-2 max-h-64 overflow-y-auto text-sm"
      />

      <div class="mt-6 flex items-center justify-between gap-2">
        <div class="flex gap-1">
          <Button
            v-if="announcements.length > 1"
            icon="i-lucide-chevron-left"
            ghost
            slate
            size="sm"
            :disabled="clampedIndex === 0"
            @click="goPrevious"
          />
          <Button
            v-if="announcements.length > 1"
            icon="i-lucide-chevron-right"
            ghost
            slate
            size="sm"
            :disabled="clampedIndex === announcements.length - 1"
            @click="goNext"
          />
        </div>
        <Button
          v-if="current.requires_ack"
          :label="t('ANNOUNCEMENTS.MODAL.ACKNOWLEDGE')"
          :is-loading="isSubmitting"
          @click="acknowledge"
        />
        <Button
          v-else
          ghost
          slate
          :label="t('ANNOUNCEMENTS.MODAL.CLOSE')"
          :is-loading="isSubmitting"
          @click="dismiss"
        />
      </div>
    </div>
  </div>
</template>
